import { watch, onMounted, onBeforeUnmount, nextTick, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

/**
 * useViewState — persisted filter/search state for list views (URL-first hybrid).
 *
 * Filters & search sync to the URL query (debounced router.replace), so the
 * history entry you navigate away from fully describes the view: Back lands on
 * the same filters, F5 survives, and filtered views are shareable. State that
 * doesn't belong in a URL (page, scroll, active tab) goes to a per-tab
 * sessionStorage bucket (`viewstate:<viewKey>`).
 *
 * Rules:
 *  - URL is canonical: on mount, query params beat stored state (deep-links win).
 *    When the URL carries none of the declared params, the bucket restores the
 *    last state and the URL is replaced to match.
 *  - One-shot command params (?select, ?action, ?focus, ?preview, ?tab, …) are
 *    never managed here; they pass through URL syncs untouched.
 *  - Empty/default = absent (no `?q=&status=` noise).
 *  - All buckets are cleared on logout / tenant switch via clearAllViewState().
 *
 * Call in component setup AFTER the refs are created but BEFORE any watchers
 * that trigger data loads — hydration mutates the refs synchronously, so loads
 * registered later see the restored values without firing twice.
 */

/** Query params that are one-shot commands consumed by views — never persisted. */
const RESERVED_PARAMS = new Set(['select', 'action', 'focus', 'preview', 'tab', 'lead']);

const STORAGE_PREFIX = 'viewstate:';

export interface QueryCodec<T> {
  ref: Ref<T>;
  dflt: T;
  parse: (raw: string) => T;
  /** undefined = omit from URL (value equals default) */
  serialize: (v: T) => string | undefined;
}

export function str<T extends string = string>(r: Ref<T>, dflt: T = '' as T): QueryCodec<T> {
  return {
    ref: r,
    dflt,
    parse: (raw) => raw as T,
    serialize: (v) => (v === dflt || v === '' ? undefined : v),
  };
}

/** For Ref<string | null> filters where null and '' both mean "no filter". */
export function strNull<T extends string = string>(r: Ref<T | null>): QueryCodec<T | null> {
  return {
    ref: r,
    dflt: null,
    parse: (raw) => (raw as T) || null,
    serialize: (v) => (v ? v : undefined),
  };
}

/** For Ref<string[]> filters; comma-separated in the URL, empty = absent. */
export function strArr(r: Ref<string[]>): QueryCodec<string[]> {
  return {
    ref: r,
    dflt: [],
    parse: (raw) => raw.split(',').map((s) => s.trim()).filter(Boolean),
    serialize: (v) => (v.length ? v.join(',') : undefined),
  };
}

export function bool(r: Ref<boolean>, dflt: boolean): QueryCodec<boolean> {
  return {
    ref: r,
    dflt,
    parse: (raw) => raw === '1' || raw === 'true',
    serialize: (v) => (v === dflt ? undefined : v ? '1' : '0'),
  };
}

export function num(r: Ref<number>, dflt: number): QueryCodec<number> {
  return {
    ref: r,
    dflt,
    parse: (raw) => {
      const n = Number(raw);
      return Number.isFinite(n) ? n : dflt;
    },
    serialize: (v) => (v === dflt ? undefined : String(v)),
  };
}

export interface ViewStateOptions {
  /** Refs synced to route.query via debounced router.replace. */
  query?: Record<string, QueryCodec<any>>;
  /** Refs persisted to the per-tab sessionStorage bucket only. */
  local?: Record<string, Ref<any>>;
  /** Scroll container to capture on leave; restore with restoreScroll() after data is ready. */
  scroll?: () => HTMLElement | null | undefined;
}

interface Bucket {
  query?: Record<string, unknown>;
  local?: Record<string, unknown>;
  scrollTop?: number;
}

function readBucket(storageKey: string): Bucket {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as Bucket) : {};
  } catch {
    return {};
  }
}

export function useViewState(viewKey: string, opts: ViewStateOptions) {
  const route = useRoute();
  const router = useRouter();
  const storageKey = `${STORAGE_PREFIX}${viewKey}`;
  const basePath = route.path;
  const queryDefs = opts.query || {};
  const localDefs = opts.local || {};

  for (const key of Object.keys(queryDefs)) {
    if (RESERVED_PARAMS.has(key)) {
      console.warn(`[useViewState] "${key}" is a reserved one-shot param and will not be managed`);
      delete queryDefs[key];
    }
  }

  const bucket = readBucket(storageKey);

  // --- Hydrate (synchronous, before any load watchers are registered) -------
  const urlHasState = Object.keys(queryDefs).some((k) => route.query[k] !== undefined);
  for (const [key, codec] of Object.entries(queryDefs)) {
    const raw = route.query[key];
    if (typeof raw === 'string') {
      codec.ref.value = codec.parse(raw);
    } else if (!urlHasState && bucket.query && bucket.query[key] !== undefined) {
      codec.ref.value = bucket.query[key];
    }
  }
  for (const [key, r] of Object.entries(localDefs)) {
    if (bucket.local && bucket.local[key] !== undefined) {
      r.value = bucket.local[key];
    }
  }

  // --- Persist ---------------------------------------------------------------
  function writeBucket(extra?: Partial<Bucket>) {
    try {
      const data: Bucket = {
        query: Object.fromEntries(Object.entries(queryDefs).map(([k, c]) => [k, c.ref.value])),
        local: Object.fromEntries(Object.entries(localDefs).map(([k, r]) => [k, r.value])),
        scrollTop: extra?.scrollTop ?? readBucket(storageKey).scrollTop,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      // Storage full/unavailable — persistence is best-effort.
    }
  }

  function syncUrl() {
    // Never navigate after the user has left this view (a stale debounce
    // firing post-navigation would yank them back).
    if (route.path !== basePath) return;
    const q: Record<string, string | string[]> = {};
    for (const [k, v] of Object.entries(route.query)) {
      if (!(k in queryDefs) && v !== undefined && v !== null) q[k] = v as string | string[];
    }
    for (const [key, codec] of Object.entries(queryDefs)) {
      const s = codec.serialize(codec.ref.value);
      if (s !== undefined) q[key] = s;
    }
    router.replace({ query: q });
  }

  let urlTimer: ReturnType<typeof setTimeout> | undefined;
  function scheduleSync() {
    writeBucket();
    if (urlTimer) clearTimeout(urlTimer);
    urlTimer = setTimeout(syncUrl, 250);
  }

  // deep: array-valued refs (strArr) may be mutated in place
  watch(
    Object.values(queryDefs).map((c) => c.ref),
    scheduleSync,
    { deep: true }
  );
  if (Object.keys(localDefs).length > 0) {
    watch(Object.values(localDefs), () => writeBucket());
  }

  onMounted(() => {
    // Bucket restored the state but the URL doesn't show it yet — make the
    // address bar canonical so F5/share reflect what's on screen.
    if (!urlHasState) {
      const hasNonDefault = Object.values(queryDefs).some(
        (c) => c.serialize(c.ref.value) !== undefined
      );
      if (hasNonDefault) syncUrl();
    }
  });

  onBeforeUnmount(() => {
    if (urlTimer) clearTimeout(urlTimer);
    const el = opts.scroll?.();
    writeBucket({ scrollTop: el ? el.scrollTop : undefined });
  });

  /** Restore the captured scroll position; call after the data has rendered. */
  async function restoreScroll() {
    const saved = readBucket(storageKey).scrollTop;
    if (!saved || !opts.scroll) return;
    await nextTick();
    requestAnimationFrame(() => {
      const el = opts.scroll?.();
      if (el) el.scrollTop = saved;
    });
  }

  /** Reset every managed ref to its default and wipe URL + bucket. */
  function clear() {
    for (const codec of Object.values(queryDefs)) codec.ref.value = codec.dflt;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
    if (urlTimer) clearTimeout(urlTimer);
    syncUrl();
  }

  return { restoreScroll, clear };
}

/** Wipe all persisted view state — call on logout and tenant switch. */
export function clearAllViewState() {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) doomed.push(key);
    }
    doomed.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
